import React, { useState, useMemo } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { ingredients } from '../../data/ingredients';
import { recipes } from '../../data/recipes';
import { Recipe } from '../../types/recipe';
import IngredientSelector from './IngredientSelector';
import MatchingRecipes from './MatchingRecipes';
import ScrollToTopButton from '../common/ScrollToTopButton';

type Props = {
  onClose: () => void;
  onRecipeSelect: (recipe: Recipe) => void;
};

export default function FridgeRecipeFinder({ onClose, onRecipeSelect }: Props) {
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);

  const handleRecipeSelect = (recipe: Recipe) => {
    // Save scroll position before navigating
    const scrollPosition = window.scrollY;
    sessionStorage.setItem('fridgeScrollPosition', scrollPosition.toString());
    onRecipeSelect(recipe);
  };

  // Restore scroll position when component mounts
  React.useEffect(() => {
    const savedPosition = sessionStorage.getItem('fridgeScrollPosition');
    if (savedPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedPosition));
        sessionStorage.removeItem('fridgeScrollPosition');
      }, 0);
    }
  }, []);

  const matchingRecipes = useMemo(() => {
    if (selectedIngredients.length === 0) return [];

    return recipes.filter(recipe => {
      const recipeIngredients = recipe.ingredients.map(ing => ing.toLowerCase());
      const selectedIngs = selectedIngredients.map(id => {
        const ingredient = ingredients.find(i => i.id === id);
        return ingredient?.name.toLowerCase() || '';
      });

      // Calculate how many selected ingredients are in the recipe
      const matchCount = selectedIngs.filter(ing => 
        recipeIngredients.some(recipeIng => recipeIng.includes(ing))
      ).length;

      // Return true if at least 50% of selected ingredients are in the recipe
      return matchCount >= (selectedIngs.length / 2);
    });
  }, [selectedIngredients]);

  const handleIngredientSelect = (id: string) => {
    setSelectedIngredients(prev => [...prev, id]);
  };

  const handleIngredientRemove = (id: string) => {
    setSelectedIngredients(prev => prev.filter(i => i !== id));
  };

  const scrollToRecipes = () => {
    const recipesSection = document.getElementById('matching-recipes');
    if (recipesSection) {
      recipesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <div className="fixed inset-0 bg-white z-50">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <button
            onClick={onClose}
            className="text-orange-500 font-medium flex items-center"
          >
            <X className="mr-1" size={20} />
            Retour
          </button>
          <span className="font-medium">Mon Frigo</span>
        </div>

        {/* Recipe suggestion banner */}
        {selectedIngredients.length > 0 && matchingRecipes.length > 0 && (
          <div className="bg-orange-50 border-b border-orange-100">
            <button
              onClick={scrollToRecipes}
              className="w-full px-4 py-3 flex items-center justify-between text-orange-700 hover:bg-orange-100 transition-colors"
            >
              <span className="font-medium">
                {matchingRecipes.length} recette{matchingRecipes.length > 1 ? 's' : ''} trouvée{matchingRecipes.length > 1 ? 's' : ''}
              </span>
              <ChevronDown size={20} className="text-orange-500" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-auto p-4">
          <IngredientSelector
            ingredients={ingredients}
            selectedIngredients={selectedIngredients}
            onSelect={handleIngredientSelect}
            onRemove={handleIngredientRemove}
          />

          {selectedIngredients.length > 0 && (
            <div id="matching-recipes">
              <MatchingRecipes
                recipes={matchingRecipes}
                onRecipeSelect={handleRecipeSelect}
              />
            </div>
          )}
        </div>
        <ScrollToTopButton />
      </div>
    </div>
  );
}